import { copyOf } from '../constants/copy-deck.prod-fixes';
import type { StagedStatus } from '../models/upload-in-progress-file';
import { convertEligible } from './convert-eligible';
import { ARCHIVE_EXTENSIONS, AUDIO_EXTENSIONS } from './media/media-extensions';

/**
 * FIX-6 (prod fixes 2026-10-02, UX-SPEC section 6): files we cannot convert at all, said up front.
 *
 * The edge decides from the bytes at upload finalize (ironman `UnsupportedInputDetector`) and returns the
 * verdict with the upload result as `{"unsupported": {"code", "hasAudio"}}`; with the server kill switch
 * `unsupportedInputDetection` off, or for any other file, the field is absent and nothing here does
 * anything -- so this module needs no flag of its own. Which target a verdict blocks mirrors shared-lib
 * `UnsupportedInput.refuses`, the rule professorx's 422 preflight and the edge fallback apply too.
 *
 * A leaf on purpose (copy deck, extension list, a type): no container, no services.
 */

export const SHOCKWAVE_DCR = 'SHOCKWAVE_DCR';
export const VECTOR_SWF = 'VECTOR_SWF';
export type UnsupportedInputCode = typeof SHOCKWAVE_DCR | typeof VECTOR_SWF;

/** `upload.unsupported_detected{kind}` values: the code lower-cased (closed set, never a file name). */
export type UnsupportedInputKind = 'shockwave_dcr' | 'vector_swf';

/** The verdict the upload result carries. */
export interface UnsupportedVerdict {
    code: string;
    hasAudio?: boolean | null;
}

/** The free player X6-2 points to; a plain link, new tab, `rel="noopener"`. */
export const RUFFLE_LINK = { text: 'ruffle.rs', href: 'https://ruffle.rs/' } as const;

export function isUnsupportedInputCode(code: unknown): code is UnsupportedInputCode {
    return code === SHOCKWAVE_DCR || code === VECTOR_SWF;
}

export function unsupportedKindOf(code: UnsupportedInputCode): UnsupportedInputKind {
    return code === SHOCKWAVE_DCR ? 'shockwave_dcr' : 'vector_swf';
}

/** The verdict on an upload result (`OriginalInputFile`), or null when there is none or it is not ours. */
export function unsupportedVerdictOf(response: unknown): (UnsupportedVerdict & { code: UnsupportedInputCode }) | null {
    const v = (response as { unsupported?: unknown } | null | undefined)?.unsupported as UnsupportedVerdict | undefined;
    if (!v || typeof v !== 'object' || !isUnsupportedInputCode(v.code)) return null;
    return { code: v.code, hasAudio: v.hasAudio === true };
}

const AUDIO_TARGETS: ReadonlySet<string> = new Set(AUDIO_EXTENSIONS.map(e => e.toLowerCase()));

/** `mp3`, `.WAV`, the `audio` hub token: an audio output. */
export function isAudioTarget(toType: unknown): boolean {
    if (typeof toType !== 'string') return false;
    const t = toType.trim().toLowerCase().replace(/^\./, '');
    return t === 'audio' || AUDIO_TARGETS.has(t);
}

const ARCHIVE_TARGETS: ReadonlySet<string> = new Set(ARCHIVE_EXTENSIONS.map(e => e.toLowerCase()));

/** `zip`, `.7Z`, `tar.gz`, the `archive` token: an output that packs the input's bytes as they are. */
export function isArchiveTarget(toType: unknown): boolean {
    if (typeof toType !== 'string') return false;
    const t = toType.trim().toLowerCase().replace(/^\./, '');
    return t === 'archive' || ARCHIVE_TARGETS.has(t);
}

/**
 * Whether this verdict blocks converting to `toType`. A Shockwave `.dcr` blocks every conversion; a vector SWF
 * blocks every conversion except an audio target when it still carries a soundtrack (`swf -> mp3` reads exactly
 * that). An archive target (archive-file-to-zip & co.) is never blocked: it packs the bytes as they are (fu-t8).
 * An unknown code blocks nothing.
 */
export function refusesTarget(verdict: UnsupportedVerdict | null | undefined, toType: unknown): boolean {
    if (!verdict || isArchiveTarget(toType)) return false;
    if (verdict.code === SHOCKWAVE_DCR) return true;
    if (verdict.code === VECTOR_SWF) return !(verdict.hasAudio === true && isAudioTarget(toType));
    return false;
}

/** X6-1 / X6-2: the reason, after the visible "Can't convert" (X6-0). */
export function unsupportedReason(code: UnsupportedInputCode): string {
    return code === SHOCKWAVE_DCR ? copyOf('X6-1') : copyOf('X6-2');
}

/**
 * The staged row's line for a refused file: kind `blocked` (left out of Convert by `convertEligible`), ⚠ plus
 * the visible "Can't convert", the reason, the Ruffle link for a SWF, and the row's own Remove. `live`: the
 * verdict arrives in the background after the upload, so it is announced politely, never as an alert.
 */
export function unsupportedStagedStatus(code: UnsupportedInputCode, onRemove: () => void): StagedStatus {
    return {
        code,
        kind: 'blocked',
        title: copyOf('X6-0'),
        text: unsupportedReason(code),
        link: code === VECTOR_SWF ? RUFFLE_LINK : undefined,
        live: true,
        action: { label: 'Remove', onClick: onRemove },
    };
}

/**
 * The website schedule's 422 refusal (professorx `UnsupportedInputPreflight`): `{"code":"UNSUPPORTED_INPUT",
 * "inputs":[1-based], "kind", "kinds"}`. Null for every other error. The backstop for an upload whose verdict
 * the page never saw (restored rows, a verdict that landed after Convert was pressed).
 */
export function unsupportedRefusalOf(err: unknown): { inputs: number[]; kinds: UnsupportedInputCode[] } | null {
    const e = err as { status?: unknown; response?: Record<string, unknown> } | null | undefined;
    const body = e?.response;
    if (e?.status !== 422 || !body || body.code !== 'UNSUPPORTED_INPUT') return null;
    const inputs = Array.isArray(body.inputs) ? body.inputs.filter((i): i is number => Number.isInteger(i) && i > 0) : [];
    const kinds = String(body.kinds ?? body.kind ?? '').split(',').map(k => k.trim()).filter(isUnsupportedInputCode);
    return { inputs, kinds };
}

/**
 * The edge fallback on a finished card: `processResult.failureCode = UNSUPPORTED` with
 * `failureDetailCode = SHOCKWAVE_DCR | VECTOR_SWF`. Structured fields only, never the human text.
 */
export function unsupportedCardFailureOf(result: unknown): UnsupportedInputCode | null {
    const r = result as { failureCode?: unknown; failureDetailCode?: unknown } | null | undefined;
    if (!r || r.failureCode !== 'UNSUPPORTED' || !isUnsupportedInputCode(r.failureDetailCode)) return null;
    return r.failureDetailCode;
}

/** Splits `text` around the first `link.text`, so the link renders as an anchor and the rest as text. */
export function splitAroundLink(text: string, link?: { text: string } | null): [string, string] | null {
    if (!link || !link.text) return null;
    const at = text.indexOf(link.text);
    if (at < 0) return null;
    return [text.slice(0, at), text.slice(at + link.text.length)];
}

/**
 * The tool page's Convert button is replaced by the rows' own Remove when every staged row is one we can't
 * convert (UX-SPEC section 6: "If it's the only file, Convert is replaced by the row's Remove button"). False
 * while anything is still being picked/added (`pending`), and for an empty page.
 */
export function nothingToConvert(staged: ({ stagedStatus?: StagedStatus | null } | null | undefined)[], pending = 0): boolean {
    if (pending > 0 || !Array.isArray(staged) || staged.filter(f => f).length === 0) return false;
    return convertEligible(staged).filter(f => f).length === 0;
}
